/**
 * ============================================================================
 * DISPUTE ANALYTICS COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Component for displaying dispute analytics
 */

import React, { useEffect, useState } from 'react';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import Spinner from '../common/Spinner';
import AnalyticsCard from './AnalyticsCard';
import SimpleChart from './SimpleChart';
import Select from '../common/Select';

/**
 * Dispute Analytics Component
 * @param {Object} props
 * @param {string} props.timeRange - Time range filter
 * @returns {JSX.Element}
 */
const DisputeAnalytics = ({ timeRange = '30d' }) => {
  const { loadDisputeAnalytics, disputeAnalytics, loading } = useAnalytics();
  const { showError } = useToast();
  const { formatNumber, language } = useI18n();
  const [selectedRange, setSelectedRange] = useState(timeRange);

  useEffect(() => {
    const loadData = async () => {
      try {
        const endDate = new Date();
        const startDate = new Date();
        
        switch (selectedRange) {
          case '7d':
            startDate.setDate(startDate.getDate() - 7);
            break;
          case '30d':
            startDate.setDate(startDate.getDate() - 30);
            break;
          case '90d':
            startDate.setDate(startDate.getDate() - 90);
            break;
          case '1y':
            startDate.setFullYear(startDate.getFullYear() - 1);
            break;
          default:
            startDate.setDate(startDate.getDate() - 30);
        }

        await loadDisputeAnalytics({
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          groupBy: selectedRange === '7d' ? 'day' : selectedRange === '30d' ? 'day' : 'week',
        });
      } catch (error) {
        showError(
          error.message || (language === 'fr' ? 'Échec du chargement des statistiques' : 'Failed to load statistics')
        );
      }
    };

    loadData();
  }, [selectedRange, loadDisputeAnalytics, showError, language]);

  if (loading && !disputeAnalytics) {
    return (
      <Card variant="glass" className="p-12">
        <div className="flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      </Card>
    );
  }

  const analytics = disputeAnalytics?.data || disputeAnalytics || {};
  const stats = analytics.stats || {};
  const chartData = analytics.chartData || [];
  const statusBreakdown = analytics.statusBreakdown || [];

  const chartDataFormatted = chartData.map((item) => ({
    label: item.label || item.date || '',
    value: item.count || item.value || 0,
    color: 'from-red-500 to-pink-500',
  }));

  const statusChartData = statusBreakdown.map((item) => ({
    label: item.status || item.label || '',
    value: item.count || item.value || 0,
    color: 'from-orange-500 to-red-500',
  }));

  return (
    <div className="space-y-6">
      {/* Header with Filter */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-black text-gray-900 dark:text-white">
          ⚠️ {language === 'fr' ? 'Analytique des Litiges' : 'Dispute Analytics'}
        </h2>
        <Select
          value={selectedRange}
          onChange={(e) => setSelectedRange(e.target.value)}
          options={[
            { value: '7d', label: language === 'fr' ? '7 jours' : '7 Days' },
            { value: '30d', label: language === 'fr' ? '30 jours' : '30 Days' },
            { value: '90d', label: language === 'fr' ? '90 jours' : '90 Days' },
            { value: '1y', label: language === 'fr' ? '1 an' : '1 Year' },
          ]}
          className="w-40"
        />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <AnalyticsCard
          title={language === 'fr' ? 'Total Litiges' : 'Total Disputes'}
          value={formatNumber(stats.total || 0)}
          icon="⚠️"
          color="from-red-500 to-pink-500"
          trend={stats.totalTrend}
          trendValue={stats.totalTrendValue}
        />
        <AnalyticsCard
          title={language === 'fr' ? 'En Cours' : 'Active'}
          value={formatNumber(stats.active || 0)}
          icon="🔄"
          color="from-yellow-500 to-orange-500"
          trend={stats.activeTrend}
          trendValue={stats.activeTrendValue}
        />
        <AnalyticsCard
          title={language === 'fr' ? 'Résolus' : 'Resolved'}
          value={formatNumber(stats.resolved || 0)}
          icon="✅"
          color="from-green-500 to-emerald-500"
          trend={stats.resolvedTrend}
          trendValue={stats.resolvedTrendValue}
        />
        <AnalyticsCard
          title={language === 'fr' ? 'Taux de Résolution' : 'Resolution Rate'}
          value={`${(stats.resolutionRate || 0).toFixed(1)}%`}
          icon="📊"
          color="from-blue-500 to-cyan-500"
          trend={stats.rateTrend}
          trendValue={stats.rateTrendValue}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {chartDataFormatted.length > 0 && (
          <SimpleChart
            title={language === 'fr' ? 'Litiges par Période' : 'Disputes Over Time'}
            data={chartDataFormatted}
            type="bar"
            height="250px"
          />
        )}
        {statusChartData.length > 0 && (
          <SimpleChart
            title={language === 'fr' ? 'Par Statut' : 'By Status'}
            data={statusChartData}
            type="bar"
            height="250px"
          />
        )}
      </div>
    </div>
  );
};

export default DisputeAnalytics;
